import { assertStaffCapability } from "./authorization.js";
import { boundedText } from "./bounded-text.js";
import { validateCatalogLocation } from "./catalog-draft.js";
import { CITY_TIME_ZONES_V1 } from "./catalog-import-normalize.js";
import { nameKey } from "./catalog-import-text.js";
import { DomainValidationError } from "./identifiers.js";
import { sha256HexOfText } from "./sha256.js";
import { ianaTimeZone } from "./time-zone.js";
import { checkExpectedVersion, violation } from "./transition-support.js";
// Places enrichment of catalog items (Phase 3.5). The vocabulary of the contract: states, decisions,
// location sources, error codes and bounds, then the rules (state transitions, budget windows,
// provenance hash). The tuples are frozen: the database CHECK constraints of the
// place tables are generated from them, and `@lafayette/schemas` repeats them (a parity test in
// `apps/api` fails when one differs).
//
// STORAGE POLICY. Lafayette stores only the provider's place id, call metadata and match decisions.
// No provider coordinate, name, address, time zone, phone, website, hours, rating, photo or review is
// stored anywhere. Canonical geography in `catalog_items` comes only from a staff confirmation with a
// declared source that is not the provider. See `docs/engineering/places.md`.
/** The one provider of 709. The value is also the `provider` of `catalog_external_refs`. */
export const PLACE_PROVIDER = "google_places";
/** The version of the provider terms this storage policy was written from (stored on every lookup). */
export const PLACE_STORAGE_POLICY = "GMP_TOS_2026_08_26";
/**
 * Where an item is in the matching flow. `CANDIDATES_FOUND`: a search found several places and staff
 * chose none (ambiguous). `NEEDS_REVIEW`: the stored place id was `NOT_FOUND` at verification.
 * `UNMATCHED`: staff cleared the match. An item that never entered the flow has no row.
 */
export const placeMatchStates = Object.freeze([
    "CANDIDATES_FOUND",
    "NO_MATCH",
    "MATCHED",
    "NO_PLACE",
    "NEEDS_REVIEW",
    "UNMATCHED",
]);
/** How fresh the verification of the place id is. Only a `MATCHED` item can be `STALE` or `ERROR`. */
export const placeRefreshStates = Object.freeze(["FRESH", "STALE", "ERROR"]);
/** One row of the append-only match history. */
export const placeMatchDecisions = Object.freeze([
    "SELECTED",
    "REPLACED",
    "CLEARED",
    "NO_PLACE",
]);
/** Why staff replaced or cleared a match. A first selection has no reason code. */
export const placeMatchReasonCodes = Object.freeze([
    "WRONG_MATCH",
    "PLACE_CLOSED",
    "PLACE_MOVED",
    "NOT_A_PLACE",
    "OTHER",
]);
export const placeLookupKinds = Object.freeze(["SEARCH_TEXT", "GET_DETAILS"]);
/** A `STARTED` row is written before each provider call, a `FINISHED` row after it. */
export const placeLookupPhases = Object.freeze(["STARTED", "FINISHED"]);
/** The Lafayette field sets. Only the adapter knows which provider fields each one asks for. */
export const placeFieldSets = Object.freeze(["SEARCH_CANDIDATES_V1", "VERIFY_ID_V1"]);
/** The billing tier of a call. No enterprise tier is allowed (a CHECK says so). */
export const placeSkuTiers = Object.freeze(["IDS_ONLY", "ESSENTIALS", "PRO"]);
export const PLACE_FIELD_SET_TIER = Object.freeze({
    SEARCH_CANDIDATES_V1: "PRO",
    VERIFY_ID_V1: "IDS_ONLY",
});
export const placeLookupOutcomes = Object.freeze(["OK", "NO_MATCH", "NOT_FOUND", "ERROR"]);
/**
 * What a provider call can fail with. `TIMEOUT`, `RATE_LIMITED`, `QUOTA_EXHAUSTED` (the daily quota or
 * the Lafayette budget) and `UNAVAILABLE` are retryable. `NOT_FOUND` (an obsolete place id),
 * `INVALID_REQUEST` and `PERMISSION_DENIED` are final.
 */
export const placesErrorCodes = Object.freeze([
    "TIMEOUT",
    "RATE_LIMITED",
    "QUOTA_EXHAUSTED",
    "NOT_FOUND",
    "INVALID_REQUEST",
    "PERMISSION_DENIED",
    "UNAVAILABLE",
]);
export const PLACES_RETRYABLE_ERROR_CODES = new Set([
    "TIMEOUT",
    "RATE_LIMITED",
    "QUOTA_EXHAUSTED",
    "UNAVAILABLE",
]);
/**
 * Where a staff member got the canonical geography of an item. The provider is NOT a source. `NONE`
 * means "nothing is known": every group is `UNKNOWN`.
 */
export const catalogLocationSources = Object.freeze([
    "SITE_VISIT",
    "VENUE_PUBLISHED",
    "SUPPLIER_PROVIDED",
    "LICENSED_DATASET",
    "IMPORT_FILE",
    "NONE",
]);
/** The state a confirmation records for one group (point, place, time zone). */
export const catalogLocationStates = Object.freeze(["SET", "UNKNOWN"]);
/**
 * What staff see for one group of an item: `CONFIRMED` (set, with a current confirmation),
 * `UNCONFIRMED` (set, but nobody confirmed it or a later edit changed it), `UNKNOWN` (explicitly
 * unknown), `NOT_ASSESSED` (empty and nobody looked).
 */
export const catalogLocationAssessments = Object.freeze([
    "CONFIRMED",
    "UNCONFIRMED",
    "UNKNOWN",
    "NOT_ASSESSED",
]);
/** The groups of the location of an item. */
export const catalogLocationGroups = Object.freeze(["point", "place", "timeZone"]);
/** What the review queue lists for the location of an item. */
export const catalogLocationReviewFilters = Object.freeze(["NOT_ASSESSED", "UNCONFIRMED"]);
/** The match states and refresh states the review queue lists. */
export const placeReviewStates = Object.freeze([
    "CANDIDATES_FOUND",
    "NO_MATCH",
    "NEEDS_REVIEW",
    "UNMATCHED",
]);
export const placeReviewRefreshStates = Object.freeze(["STALE", "ERROR"]);
export const PLACE_ID_MAX = 255;
export const PLACE_SEARCH_TEXT_MAX = 200;
/** Candidates a search returns when the caller does not say. */
export const PLACE_DEFAULT_CANDIDATES = 5;
export const PLACE_MAX_CANDIDATES = 10;
export const PLACE_NEAR_RADIUS_MIN_METERS = 100;
export const PLACE_NEAR_RADIUS_MAX_METERS = 50_000;
/** A search result can be chosen from for this long. */
export const PLACE_SEARCH_VALID_MINUTES = 30;
/** A matched place id is verified again after this many days (Google advises at most 12 months). */
export const PLACE_VERIFY_INTERVAL_DAYS = 365;
/** A match whose last verification is older than this is shown as `STALE`, before the job runs. */
export const PLACE_STALE_AFTER_DAYS = 400;
/** The rows of match history and of lookup history a place view lists. */
export const PLACE_VIEW_HISTORY_LIMIT = 20;
export const PLACE_REVIEW_LIMIT_MAX = 100;
/** The bounds of the provider budget (owner decision: 30 calls a minute, 500 calls a UTC day). */
export const PLACES_LIMITS = Object.freeze({ callsPerMinute: 30, callsPerDay: 500 });
// --- rules (pure) ---------------------------------------------------------------------------------------
const DAY_MS = 86_400_000;
// A rule in this file never reads a clock, a random source or a provider: the caller passes `now` and,
// where jitter is wanted, `random`. The text of a search, a candidate or any provider value never enters
// a result that is stored.
/** The default text of a search: the item name, then its locality. At most 200 characters. */
export function placeSearchText(input) {
    if (input.text !== undefined && input.text !== null) {
        return boundedText("text", input.text, PLACE_SEARCH_TEXT_MAX);
    }
    const combined = input.locality === null ? input.itemName : `${input.itemName}, ${input.locality}`;
    const trimmed = [...combined.trim()].slice(0, PLACE_SEARCH_TEXT_MAX).join("").trim();
    return boundedText("text", trimmed, PLACE_SEARCH_TEXT_MAX);
}
/** The hash a lookup stores instead of the search text. */
export const placeSearchQuerySha256 = (text) => sha256HexOfText(text.trim());
/** A bias circle: a real point and a radius of 100 to 50,000 meters. */
export function validatePlaceSearchNear(near) {
    validateCatalogLocation(null, near.latitude, near.longitude);
    if (!(near.radiusMeters >= PLACE_NEAR_RADIUS_MIN_METERS) ||
        !(near.radiusMeters <= PLACE_NEAR_RADIUS_MAX_METERS)) {
        throw new DomainValidationError("radiusMeters", `must be ${PLACE_NEAR_RADIUS_MIN_METERS} to ${PLACE_NEAR_RADIUS_MAX_METERS}`);
    }
    return near;
}
/** The start of the sliding minute and of the UTC day that the budget counts from. */
export function placeBudgetWindows(now) {
    return {
        minuteSince: new Date(now.getTime() - 60_000),
        daySince: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())),
    };
}
export function nextUtcMidnight(now) {
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) + DAY_MS);
}
/**
 * `null` when one more call fits. The day cap wins over the minute cap (`QUOTA_EXHAUSTED`: wait for the
 * next UTC day); a full minute is `RATE_LIMITED` (wait a minute).
 */
export function checkPlaceBudget(counts, limits = PLACES_LIMITS) {
    if (counts.today >= limits.callsPerDay)
        return "QUOTA_EXHAUSTED";
    if (counts.lastMinute >= limits.callsPerMinute)
        return "RATE_LIMITED";
    return null;
}
// --- states ---------------------------------------------------------------------------------------------
/**
 * The refresh state to show. A matched item whose last verification is older than 400 days is `STALE`
 * even before a job ran. Only a matched item can be stale or in error.
 */
export function derivedPlaceRefreshState(row, now) {
    if (row.matchState !== "MATCHED")
        return "FRESH";
    if (row.refreshState === "FRESH" &&
        row.lastVerifiedAt !== null &&
        now.getTime() - row.lastVerifiedAt.getTime() > PLACE_STALE_AFTER_DAYS * DAY_MS) {
        return "STALE";
    }
    return row.refreshState;
}
/**
 * The state after a search. A matched item, one that needs review and one staff marked "no place" keep
 * their state: a search is a lookup, not a decision. Otherwise any candidate asks for a person
 * (`CANDIDATES_FOUND`) and none is `NO_MATCH`.
 */
export function placeStateAfterSearch(current, candidateCount) {
    if (current === "MATCHED" || current === "NEEDS_REVIEW" || current === "NO_PLACE")
        return current;
    return candidateCount > 0 ? "CANDIDATES_FOUND" : "NO_MATCH";
}
/** `catalog.edit`, an editable status and a current version: the checks every place command makes first. */
function placeGate(input) {
    assertStaffCapability(input.actor, "catalog.edit", input.environment);
    if (input.item.status === "ARCHIVED")
        throw violation("catalog.archived");
    checkExpectedVersion(input.actor, input.expectedVersion, input.item.version, "catalog.version_conflict");
}
/**
 * Chooses one candidate of one search. The place id must be in that search's stored candidates, in
 * the order they were returned (the rank is the 1-based position), and the search must be younger than
 * 30 minutes. An item that already has a place id needs a correction (a replace); one without must not
 * send one.
 */
export function planPlaceSelection(input) {
    placeGate(input);
    if (input.search === null)
        throw violation("catalog.place_candidate_not_found");
    if (input.now.getTime() - input.search.finishedAt.getTime() >
        PLACE_SEARCH_VALID_MINUTES * 60_000) {
        throw violation("catalog.place_search_expired");
    }
    const index = input.search.candidatePlaceIds.indexOf(input.placeId);
    if (index < 0)
        throw violation("catalog.place_candidate_not_found");
    if (input.currentPlaceId !== null && input.correction === null) {
        throw violation("catalog.place_already_matched");
    }
    if (input.currentPlaceId === null && input.correction !== null) {
        throw new DomainValidationError("correction", "is only for an item that has a match");
    }
    if (input.currentPlaceId === input.placeId)
        throw violation("catalog.place_already_matched");
    return {
        decision: input.currentPlaceId === null ? "SELECTED" : "REPLACED",
        placeId: input.placeId,
        previousPlaceId: input.currentPlaceId,
        candidateRank: index + 1,
        reasonCode: input.correction?.reasonCode ?? null,
        reason: input.correction === null ? null : boundedText("reason", input.correction.reason, 500),
    };
}
/** Removes the match, or says that the item has no physical place. Clearing nothing is `catalog.place_not_matched`. */
export function planPlaceClear(input) {
    placeGate(input);
    if (input.currentPlaceId === null && !input.noPlace)
        throw violation("catalog.place_not_matched");
    return {
        decision: input.noPlace ? "NO_PLACE" : "CLEARED",
        previousPlaceId: input.currentPlaceId,
        reasonCode: input.reasonCode,
        reason: boundedText("reason", input.reason, 500),
        nextState: input.noPlace ? "NO_PLACE" : "UNMATCHED",
    };
}
// --- verification ---------------------------------------------------------------------------------------
/** 30 s, 60 s, 120 s ... up to 1 hour, then full jitter (`random()` from 0 to 1). At least 1 s. */
export function placeVerifyDelayMs(attempt, random = Math.random) {
    const ceiling = Math.min(3_600_000, 30_000 * 2 ** Math.max(0, attempt - 1));
    return Math.max(1_000, Math.floor(ceiling * random()));
}
/**
 * What one verification attempt does to the enrichment state and to the intent. The item itself is
 * never touched: a provider failure changes only these columns. `exists`: fresh for another 365 days.
 * `NOT_FOUND`: the stored id is obsolete, so staff must look (the id is kept until they correct it).
 * A retryable error makes the match stale and asks for a retry; a final error, or the last attempt,
 * makes it `ERROR`.
 */
export function planPlaceVerification(input) {
    const { current, result, now } = input;
    if (result.kind === "exists") {
        return {
            next: {
                matchState: "MATCHED",
                refreshState: "FRESH",
                lastVerifiedAt: now,
                nextVerifyAt: new Date(now.getTime() + PLACE_VERIFY_INTERVAL_DAYS * DAY_MS),
                consecutiveFailures: 0,
                lastErrorCode: null,
            },
            audit: null,
            job: { kind: "done" },
        };
    }
    if (result.code === "NOT_FOUND") {
        return {
            next: {
                matchState: "NEEDS_REVIEW",
                refreshState: "FRESH",
                lastVerifiedAt: current.lastVerifiedAt,
                nextVerifyAt: null,
                consecutiveFailures: 0,
                lastErrorCode: null,
            },
            audit: { action: "needs_review", outcome: "NOT_FOUND" },
            job: { kind: "done" },
        };
    }
    const failures = Math.min(20, current.consecutiveFailures + 1);
    const retryable = PLACES_RETRYABLE_ERROR_CODES.has(result.code);
    const exhausted = input.attemptNumber >= input.maxAttempts;
    if (retryable && !exhausted) {
        const afterMs = result.code === "QUOTA_EXHAUSTED"
            ? nextUtcMidnight(now).getTime() - now.getTime() + placeVerifyDelayMs(1, input.random)
            : placeVerifyDelayMs(input.attemptNumber, input.random);
        return {
            next: {
                ...current,
                matchState: "MATCHED",
                refreshState: "STALE",
                consecutiveFailures: failures,
                lastErrorCode: result.code,
            },
            audit: null,
            job: { kind: "retry", errorCode: result.code, afterMs },
        };
    }
    return {
        next: {
            ...current,
            matchState: "MATCHED",
            refreshState: "ERROR",
            consecutiveFailures: failures,
            lastErrorCode: result.code,
        },
        audit: { action: "verification_failed", errorCode: result.code, final: true },
        job: { kind: "failed", errorCode: result.code },
    };
}
/** SHA-256 of the canonical JSON of the location values, in the fixed order of the stored columns. */
export function catalogLocationValuesSha256(values) {
    return sha256HexOfText(JSON.stringify([
        values.latitude,
        values.longitude,
        values.locality,
        values.region,
        values.countryCode,
        values.timeZone,
        values.addressText,
    ]));
}
const round6 = (value) => Math.round(value * 1_000_000) / 1_000_000;
/**
 * A staff confirmation of the whole location. Every group is a value or `UNKNOWN`: nothing is kept as
 * it was. The source is never the provider (there is no such entry), `NONE` means every group is
 * `UNKNOWN`, and any other source needs a value. Coordinates are rounded to 6 decimals.
 */
export function planCatalogLocationConfirmation(input) {
    placeGate(input);
    const { point, place, timeZone, source } = input.confirmation;
    if (!catalogLocationSources.includes(source)) {
        throw new DomainValidationError("source", "must be a declared source");
    }
    const anyValue = point !== "UNKNOWN" || place !== "UNKNOWN" || timeZone !== "UNKNOWN";
    if ((source === "NONE") === anyValue) {
        throw new DomainValidationError("source", "NONE needs every group UNKNOWN, and any other source needs a value");
    }
    let latitude = null;
    let longitude = null;
    if (point !== "UNKNOWN") {
        const checked = validateCatalogLocation(null, point.latitude, point.longitude);
        latitude = round6(checked.latitude);
        longitude = round6(checked.longitude);
    }
    let locality = null;
    let region = null;
    let countryCode = null;
    let addressText = null;
    if (place !== "UNKNOWN") {
        locality = boundedText("locality", place.locality, 100);
        region = place.region == null ? null : boundedText("region", place.region, 100);
        countryCode = validateCatalogLocation(place.countryCode, null, null).countryCode;
        addressText =
            place.addressText == null ? null : boundedText("addressText", place.addressText, 300);
    }
    const zone = timeZone === "UNKNOWN" ? null : ianaTimeZone(timeZone, "timeZone");
    const patch = { latitude, longitude, locality, region, countryCode, timeZone: zone, addressText };
    return {
        patch,
        source,
        pointState: point === "UNKNOWN" ? "UNKNOWN" : "SET",
        placeState: place === "UNKNOWN" ? "UNKNOWN" : "SET",
        timeZoneState: timeZone === "UNKNOWN" ? "UNKNOWN" : "SET",
        valuesSha256: catalogLocationValuesSha256(patch),
    };
}
/**
 * What staff see per group, from the item's values and its newest confirmation. The confirmation is
 * current only while the values still hash the same: a later plain edit makes a set group
 * `UNCONFIRMED` and an empty one `NOT_ASSESSED`, with no change to the editor.
 */
export function assessCatalogLocation(values, newest) {
    const current = newest !== null && newest.valuesSha256 === catalogLocationValuesSha256(values) ? newest : null;
    const assess = (isSet, state) => {
        if (isSet)
            return current !== null && state === "SET" ? "CONFIRMED" : "UNCONFIRMED";
        return current !== null && state === "UNKNOWN" ? "UNKNOWN" : "NOT_ASSESSED";
    };
    return {
        point: assess(values.latitude !== null, current?.pointState),
        place: assess(values.locality !== null || values.region !== null || values.countryCode !== null, current?.placeState),
        timeZone: assess(values.timeZone !== null, current?.timeZoneState),
        source: current === null ? null : current.locationSource,
    };
}
/** A zone from Lafayette's own city table (never from the provider), a hint for the zone field only. */
export function suggestedTimeZone(locality, countryCode) {
    if (locality === null)
        return null;
    const city = CITY_TIME_ZONES_V1[nameKey(locality)];
    if (city === undefined)
        return null;
    return countryCode === null || city.countryCode === countryCode ? city.timeZone : null;
}
